import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createTrip } from '../services/firebaseService';
import { ArrowLeft, Plus, X, Globe, Users, FileText, MapPin } from 'lucide-react';
import logo from '../assets/logo.png';

const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
];

const CreateTrip = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    currency: 'USD',
    members: ['']
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('tripDraft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        const draftAge = Date.now() - new Date(draftData.savedAt).getTime();
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (draftAge < maxAge) {
          setFormData({
            name: draftData.name || '',
            description: draftData.description || '',
            currency: draftData.currency || 'USD',
            members: draftData.members || ['']
          });
          setIsDraft(true);
          console.log('📝 Auto-loaded draft:', draftData.name);
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Mark as draft when user makes changes
    setIsDraft(true);
  };

  const handleMemberChange = (index, value) => {
    const newMembers = [...formData.members];
    newMembers[index] = value;
    setFormData(prev => ({ ...prev, members: newMembers }));
  };

  const addMember = () => {
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, '']
    }));
    
    // Mark as draft when user adds members
    setIsDraft(true);
  };

  const removeMember = (index) => {
    if (formData.members.length > 1) {
      const newMembers = formData.members.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, members: newMembers }));
      
      // Mark as draft when user removes members
      setIsDraft(true);
    }
  };

  const saveDraft = () => {
    // Save form data to localStorage
    try {
      const draftData = {
        ...formData,
        savedAt: new Date().toISOString(),
        savedBy: currentUser.email
      };
      localStorage.setItem('tripDraft', JSON.stringify(draftData));
      setIsDraft(false);
      console.log('✅ Draft saved:', draftData);
      alert('Draft saved! You can come back later to complete this trip.');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft. Please try again.');
    }
  };

  const loadDraft = () => {
    try {
      const savedDraft = localStorage.getItem('tripDraft');
      if (savedDraft) {
        const draftData = JSON.parse(savedDraft);
        console.log('📝 Loading draft:', draftData);
        
        // Check if draft is not too old (24 hours)
        const draftAge = Date.now() - new Date(draftData.savedAt).getTime();
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (draftAge < maxAge) {
          setFormData({
            name: draftData.name || '',
            description: draftData.description || '',
            currency: draftData.currency || 'USD',
            members: draftData.members || ['']
          });
          setIsDraft(true);
          alert('Draft loaded! Review and complete your trip.');
        } else {
          // Remove old draft
          localStorage.removeItem('tripDraft');
          alert('Draft is too old and has been removed.');
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Trip name is required';
    }
    
    // Description is now optional - remove validation
    
    const validMembers = formData.members.filter(member => member.trim());
    if (validMembers.length < 1) {
      newErrors.members = 'At least 1 member is required (add yourself if needed)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      console.log('Creating trip with data:', formData);
      console.log('Current user:', currentUser);
      
      const validMembers = formData.members.filter(member => member.trim());
      
      const tripData = {
        name: formData.name,
        description: formData.description || '', // Optional description
        currency: formData.currency,
        members: validMembers, // Only include manually added members
        createdBy: currentUser.email,
        totalSpent: 0
      };
      
      console.log('Trip data to save:', tripData);
      
      const tripId = await createTrip(tripData);
      console.log('Trip created successfully with ID:', tripId);
      
      // Clear draft after successful creation
      localStorage.removeItem('tripDraft');
      setIsDraft(false);
      
      // Show success and redirect
      navigate(`/trip/${tripId}`);
    } catch (error) {
      console.error('Error creating trip:', error);
      let errorMessage = 'Failed to create trip. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your Firebase permissions.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Premium Background with Textures */}
      <div className="absolute inset-0 bg-gradient-to-br from-olive-900 via-olive-800 to-emerald-900">
        {/* Texture Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Gradient Overlay for Depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-olive-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-emerald-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-600/20 rounded-full blur-2xl animate-pulse delay-500"></div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Premium Header */}
        <header className="bg-white/10 backdrop-blur-2xl shadow-2xl border-b border-white/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-800 hover:text-gray-900 transition-colors font-medium"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-lg opacity-30"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/20 p-8 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl"></div>
              <div className="relative">
                <div className="mb-8">
                  <h1 className="text-3xl font-black text-gray-800 mb-2" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.05em' }}>
                    Create New Trip
                    {isDraft && (
                      <span className="ml-3 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Draft</span>
                    )}
                  </h1>
                  <p className="text-gray-700">Set up your group expense tracking for a new adventure</p>
                </div>

                {errors.submit && (
                  <div className="mb-6 p-4 bg-red-500/20 backdrop-blur border border-red-500/30 rounded-xl flex items-center">
                    <span className="text-red-100 text-sm">{errors.submit}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Trip Name */}
                  <div>
                    <label className="block text-gray-800 text-sm font-medium mb-2">
                      <MapPin className="inline w-4 h-4 mr-2 text-olive-600" />
                      Trip Name
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`relative w-full px-4 py-4 bg-white/20 backdrop-blur border rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800 placeholder-gray-500 z-10 ${
                          errors.name ? 'border-red-400/50' : 'border-white/30'
                        }`}
                        placeholder="e.g., Europe Adventure 2024"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-gray-800 text-sm font-medium mb-2">
                      <FileText className="inline w-4 h-4 mr-2 text-olive-600" />
                      Description (Optional)
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="relative w-full px-4 py-4 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800 placeholder-gray-500 z-10"
                        placeholder="Describe your trip and what you'll be doing..."
                      />
                    </div>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-gray-800 text-sm font-medium mb-2">
                      <Globe className="inline w-4 h-4 mr-2 text-olive-600" />
                      Currency
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className="relative w-full px-4 py-4 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800 z-10 appearance-none"
                      >
                        {currencies.map((currency) => (
                          <option key={currency.code} value={currency.code} className="bg-gray-800 text-white">
                            {currency.symbol} {currency.code} - {currency.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Members */}
                  <div>
                    <label className="block text-gray-800 text-sm font-medium mb-2">
                      <Users className="inline w-4 h-4 mr-2 text-olive-600" />
                      Members
                    </label>
                    <div className="space-y-3">
                      {formData.members.map((member, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="relative group flex-1">
                            <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                            <input
                              type="text"
                              value={member}
                              onChange={(e) => handleMemberChange(index, e.target.value)}
                              className="relative w-full px-4 py-4 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-transparent text-gray-800 placeholder-gray-500 z-10"
                              placeholder={`Member ${index + 1}`}
                            />
                          </div>
                          {formData.members.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMember(index)}
                              className="p-3 bg-red-500/20 backdrop-blur border border-red-400/30 rounded-xl hover:bg-red-500/30 transition-colors"
                            >
                              <X className="w-5 h-5 text-red-400" />
                            </button>
                          )}
                        </div>
                      ))}
                      {errors.members && (
                        <p className="text-sm text-red-400">{errors.members}</p>
                      )}
                      <button
                        type="button"
                        onClick={addMember}
                        className="flex items-center px-4 py-3 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white font-bold rounded-xl hover:from-gray-900 hover:via-gray-800 hover:to-gray-900 transition-all duration-300 shadow-xl hover:shadow-2xl"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Member
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="flex-1 px-6 py-4 bg-white/20 backdrop-blur border border-white/30 text-gray-800 font-medium rounded-xl hover:bg-white/30 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white font-bold rounded-xl hover:from-green-900 hover:via-green-800 hover:to-green-900 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating Trip...
                        </span>
                      ) : (
                        'Create Trip'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreateTrip;
